import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { signInWithOAuth } from '../../lib/oauth';
import { findQrSession, markQrSessionUsed } from '../../db/qrSessionRepository';
import { findPdfsByUser } from '../../db/pdfRepository';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // 모바일 감지
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 저장된 로그인 정보 불러오기
  useEffect(() => {
    const savedEmail = localStorage.getItem('saved_email');
    const savedPassword = localStorage.getItem('saved_password');
    const savedRememberMe = localStorage.getItem('remember_me') === 'true';

    if (savedEmail && savedPassword && savedRememberMe) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        // 로그인 상태 유지 처리
        if (rememberMe) {
          localStorage.setItem('saved_email', email);
          localStorage.setItem('saved_password', password);
          localStorage.setItem('remember_me', 'true');
        } else {
          localStorage.removeItem('saved_email');
          localStorage.removeItem('saved_password');
          localStorage.removeItem('remember_me');
        }

        // 대기 중인 QR 토큰이 있는지 확인 (QR 스캔 후 로그인한 경우)
        const pendingToken = localStorage.getItem('pending_qr_token');
        if (pendingToken) {
          localStorage.removeItem('pending_qr_token');

          // QR 세션 유효성 확인 후 연결
          const session = await findQrSession(pendingToken);

          if (session && !session.is_used && new Date(session.expires_at) > new Date()) {
            await markQrSessionUsed(pendingToken, data.user.id);

            localStorage.setItem('qr_auto_play', 'true');
            localStorage.setItem('qr_session_token', pendingToken);
            localStorage.setItem('qr_user_id', data.user.id);
            navigate('/dashboard?mode=remote');
            return;
          }
        }

        // 사용자의 PDF 자동 재생 설정 확인
        const pdfs = await findPdfsByUser(data.user.id);
        const autoPlayPdf = pdfs.find((p) => p.auto_fullscreen);

        if (autoPlayPdf && isMobile) {
          navigate(`/presentations/${autoPlayPdf.id}/remote`);
          return;
        }

        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message || t('auth.login_failed'));
    } finally {
      setLoading(false);
    }
  };

  // 소셜 로그인
  const handleSocialLogin = async (provider: 'google' | 'kakao') => {
    try {
      await signInWithOAuth(provider);
    } catch (err: any) {
      setError(err.message || t('home.social_login_failed'));
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* 로고 */}
        <div className="flex justify-center mb-8">
          <Link to="/">
            <img
              src="https://static.readdy.ai/image/fa9e51599f3a91cf824c0f1e50ffec3c/bbe0145b0a3abb3ea6b08059cd830213.png"
              alt="Qpresen Logo"
              className="h-20 object-contain hover:opacity-80 transition-opacity"
            />
          </Link>
        </div>

        {/* 로그인 폼 */}
        <form onSubmit={handleLogin} className="space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all text-sm bg-gray-50"
              placeholder={t('home.email_placeholder')}
            />
          </div>

          <div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all text-sm bg-gray-50"
              placeholder={t('home.password_placeholder')}
            />
          </div>

          {/* 로그인 상태 유지 */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500 cursor-pointer"
            />
            <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-600 cursor-pointer">
              {t('home.remember_me')}
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-violet-500 to-purple-500 text-white py-3.5 rounded-lg font-medium hover:from-violet-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-sm"
          >
            {loading ? t('home.logging_in') : t('home.login_button')}
          </button>
        </form>

        {/* 링크들 */}
        <div className="flex items-center justify-center gap-4 mt-4 text-sm">
          <button className="text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap">
            {t('home.find_id')}
          </button>
          <span className="text-gray-300">|</span>
          <button className="text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap">
            {t('home.find_password')}
          </button>
          <span className="text-gray-300">|</span>
          <Link to="/signup" className="text-gray-500 hover:text-gray-700 whitespace-nowrap">
            {t('home.signup_with_email')}
          </Link>
        </div>

        {/* 구분선 */}
        <div className="flex items-center my-6">
          <div className="flex-1 border-t border-gray-200"></div>
          <span className="px-4 text-xs text-gray-400 whitespace-nowrap">{t('home.sns_login_divider')}</span>
          <div className="flex-1 border-t border-gray-200"></div>
        </div>

        {/* 소셜 로그인 버튼들 */}
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => handleSocialLogin('google')}
            className="w-11 h-11 flex items-center justify-center rounded-full border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </button>
          <button
            onClick={() => handleSocialLogin('kakao')}
            className="w-11 h-11 flex items-center justify-center rounded-full bg-yellow-400 hover:bg-yellow-500 transition-colors cursor-pointer"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#3C1E1E">
              <path d="M12 3C6.477 3 2 6.463 2 10.691c0 2.65 1.734 4.974 4.38 6.308-.193.72-.7 2.607-.802 3.01-.125.5.184.493.386.359.159-.105 2.53-1.712 3.554-2.405.49.072.994.11 1.482.11 5.523 0 10-3.463 10-7.382C22 6.463 17.523 3 12 3z"/>
            </svg>
          </button>
        </div>

        {/* 뒤로가기 버튼 */}
        <Link
          to="/"
          className="w-full mt-6 py-2.5 text-sm text-gray-500 hover:text-gray-700 transition-colors block text-center"
        >
          {t('home.go_back')}
        </Link>
      </div>
    </div>
  );
}
